import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Globe, Plus, Trash2, RefreshCw, X, Save, Shield, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const DomainManager = () => {
  const { api } = useAuth();
  const [domains, setDomains] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [domain, setDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  const load = () => api.get('/admin/domains').then(r => setDomains(r.data || [])).catch(console.error);
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setAdding(true);
    try {
      await api.post('/admin/domains', { domain: domain.trim() });
      setDomain(''); setShowForm(false); load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error adding domain');
    }
    setAdding(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove domain ${name}? This will remove Nginx config.`)) return;
    await api.delete(`/admin/domains/${id}`); load();
  };

  const handleRefreshSSL = async (id) => {
    setRefreshing(id);
    try {
      await api.post(`/admin/domains/${id}/refresh-ssl`);
      load();
    } catch (err) { alert('SSL refresh failed'); }
    setRefreshing(null);
  };

  const getStatusBadge = (d) => {
    if (d.status === 'active') return <span className="badge badge-success"><CheckCircle size={10} /> Active</span>;
    if (d.status === 'setting_up') return <span className="badge badge-warning"><Loader size={10} /> Setting up...</span>;
    return <span className="badge badge-danger"><AlertCircle size={10} /> {d.status}</span>;
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><Globe size={20} /> Domain Manager ({domains.length})</h2>
        <button className="btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Add Domain</button>
      </div>

      <div className="admin-card" style={{ marginBottom: 16, padding: 20, background: "rgba(245,158,11,0.04)", borderColor: "rgba(245,158,11,0.1)" }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, color: "#f59e0b", marginBottom: 12 }}>📋 Cara Setup Domain Baru</h3>
        <div style={{ fontSize: 12, color: "#aaa", lineHeight: 2 }}>
          <div>1️⃣ Beli domain baru di registrar (Namecheap, GoDaddy, Niagahoster, dll)</div>
          <div>2️⃣ Login ke DNS management di registrar kamu</div>
          <div>3️⃣ Tambah <strong style={{color:"#ddd"}}>A Record</strong>: Name <code style={{color:"#4caf50",background:"rgba(76,175,80,0.1)",padding:"2px 6px",borderRadius:4}}>@</code> → Value <code style={{color:"#f59e0b",background:"rgba(245,158,11,0.1)",padding:"2px 6px",borderRadius:4}}>159.223.52.92</code></div>
          <div>4️⃣ Tambah <strong style={{color:"#ddd"}}>A Record</strong>: Name <code style={{color:"#4caf50",background:"rgba(76,175,80,0.1)",padding:"2px 6px",borderRadius:4}}>www</code> → Value <code style={{color:"#f59e0b",background:"rgba(245,158,11,0.1)",padding:"2px 6px",borderRadius:4}}>159.223.52.92</code></div>
          <div>5️⃣ Tunggu propagasi DNS (5-30 menit)</div>
          <div>6️⃣ Kembali ke sini → klik <strong style={{color:"#f59e0b"}}>Add Domain</strong> → masukkan domain → klik Setup</div>
          <div>7️⃣ Otomatis setup Nginx + SSL (HTTPS) ✅</div>
        </div>
        <div style={{ marginTop: 12, padding: 10, background: "rgba(239,68,68,0.06)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.1)" }}>
          <p style={{ fontSize: 11, color: "#ef4444", margin: 0 }}>⚠️ <strong>Penting:</strong> DNS harus sudah pointing ke IP server sebelum setup. Kalau belum, SSL akan gagal.</p>
        </div>
      </div>

      {showForm && (
        <div className="admin-card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>Add New Domain</h3>
            <button className="btn-sm btn-delete" onClick={() => setShowForm(false)}><X size={14} /></button>
          </div>
          <form onSubmit={handleAdd}>
            <label className="form-label">Domain Name</label>
            <input className="form-input" value={domain} onChange={e => setDomain(e.target.value)} required
              placeholder="e.g. bokep-baru.com (tanpa http/www)" />
            <label className="form-label" style={{marginTop:12}}>Domain Type</label>
            <div style={{display:"flex",gap:8}}>
              <button type="button" className={`admin-tab ${domainType==="website"?"active":""}`} onClick={()=>setDomainType("website")}>🌐 Website</button>
              <button type="button" className={`admin-tab ${domainType==="admin"?"active":""}`} onClick={()=>setDomainType("admin")}>🔒 Admin Panel</button>
            </div>
            <p style={{fontSize:10,color:"#555",marginTop:4}}>{domainType==="admin"?"Domain akan auto-redirect ke /admin":"Domain untuk website publik + /admin"}</p>
            <p style={{ fontSize: 10, color: '#555', marginTop: 4 }}>Pastikan DNS A record sudah diarahkan ke 159.223.52.92 sebelum menambahkan</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn-primary" style={{ marginTop: 0 }} disabled={adding}>
                <Save size={16} /> {adding ? 'Setting up...' : 'Setup Domain'}
              </button>
              <button type="button" className="btn-secondary" style={{ marginTop: 0 }} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Current domains */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <h3 className="card-subtitle">Current Domains</h3>
        <div style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Globe size={14} style={{ color: '#4caf50' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4caf50' }}>raja-bokep.com</span>
          </div>
          <span className="badge badge-success">Primary</span>
          <span className="badge badge-success"><Shield size={10} /> SSL</span>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Globe size={14} style={{ color: '#4caf50' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4caf50' }}>adminrajabokep.com</span>
          </div>
          <span className="badge badge-info">Admin</span>
          <span className="badge badge-success"><Shield size={10} /> SSL</span>
        </div>
      </div>

      {/* Added domains */}
      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr><th>Domain</th><th>Type</th><th>Status</th><th>SSL</th><th>Added</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#555' }}>No additional domains. Add a new domain when needed.</td></tr>
            ) : domains.map(d => (
              <tr key={d.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Globe size={14} style={{ color: d.status === 'active' ? '#4caf50' : '#ef4444' }} />
                    <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: '#f59e0b', textDecoration: 'none' }}>{d.domain}</a>
                  </div>
                </td>
                <td><span className="badge badge-info">{d.type === "admin" ? "🔒 Admin" : "🌐 Website"}</span></td><td>{getStatusBadge(d)}</td>
                <td>
                  {d.ssl ? (
                    <span className="badge badge-success"><Shield size={10} /> Active</span>
                  ) : (
                    <span className="badge badge-danger">No SSL</span>
                  )}
                </td>
                <td style={{ fontSize: 11, color: '#555' }}>{new Date(d.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-sm btn-edit" onClick={() => handleRefreshSSL(d.id)} disabled={refreshing === d.id}>
                      <RefreshCw size={12} className={refreshing === d.id ? 'spin' : ''} /> SSL
                    </button>
                    <button className="btn-sm btn-delete" onClick={() => handleDelete(d.id, d.domain)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DomainManager;
